import type { CategoryChipData } from "@models/category";
import { ErrorState, Skeleton } from "@common/index";
import { CategoryChip } from "./CategoryChip";
import { SectionHeader } from "./SectionHeader";

interface CategoryChipRowProps {
    title: string;
    categories: CategoryChipData[];
    viewAllHref?: string;
    isLoading?: boolean;
    /**
     * The row's read failed and there is nothing held to show. Rendered as a
     * distinct failed state with Retry — never as an empty row (same rule as
     * ProductCarouselRow).
     */
    hasError?: boolean;
    onRetry?: () => void;
    errorMessage?: string;
    /** Placeholder chips shown while loading. Match the expected result count. */
    skeletonCount?: number;
}

const DEFAULT_SKELETON_COUNT = 8;

export const CategoryChipRow = ({
    title,
    categories,
    viewAllHref,
    isLoading = false,
    hasError = false,
    onRetry,
    errorMessage,
    skeletonCount = DEFAULT_SKELETON_COUNT,
}: CategoryChipRowProps) => {
    const showSkeletons = isLoading && categories.length === 0;
    // Loaded chips win over the flag: a later failure must not blank a row
    // that has something to show.
    const showError = hasError && categories.length === 0 && !isLoading;

    return (
        <section className="py-4">
            <SectionHeader title={title} viewAllHref={viewAllHref} />
            {showError ? (
                <ErrorState message={errorMessage} onRetry={onRetry} />
            ) : (
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-2 pb-2">
                    {showSkeletons
                        ? Array.from({ length: skeletonCount }).map((_, i) => (
                            <div
                                key={i}
                                data-testid="category-chip-skeleton"
                                className="snap-start flex flex-col items-center gap-2 w-20 shrink-0"
                            >
                                <Skeleton shape="circle" className="w-16 h-16" />
                                <Skeleton shape="text" className="w-14" />
                            </div>
                        ))
                        : categories.map((cat) => (
                            <div key={cat.id} className="snap-start">
                                <CategoryChip category={cat} />
                            </div>
                        ))}
                </div>
            )}
        </section>
    );
};
